import React, { useState, useRef } from 'react';
import { Card, Input, Button } from '../../components/ui';
import { Printer, Settings, Tag } from 'lucide-react';
import { cn } from '../../lib/utils';

export function SilentAuctionBidSheetGenerator() {
  const [orgName, setOrgName] = useState('School PTA');
  const [itemName, setItemName] = useState('Gift Basket');
  const [itemValue, setItemValue] = useState('100');
  const [startingBid, setStartingBid] = useState('25');
  const [increment, setIncrement] = useState('5');
  const [numRows, setNumRows] = useState('15');
  const [buyItNow, setBuyItNow] = useState('');

  const rows = parseInt(numRows) || 15;
  const start = parseFloat(startingBid) || 0;
  const inc = parseFloat(increment) || 0;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      {/* Editor Panel - Hidden during print */}
      <div className="space-y-6 print:hidden">
        <Card className="p-6 space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <Settings className="w-5 h-5 text-indigo-500" />
            <h3 className="text-xl font-bold font-display text-slate-900 dark:text-white">Sheet Settings</h3>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Organization Name</label>
              <Input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g., Spring Gala 2026" />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Item Name</label>
                <Input value={itemName} onChange={e => setItemName(e.target.value)} placeholder="Spa Day Package" />
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Item Value ($)</label>
                <Input type="number" value={itemValue} onChange={e => setItemValue(e.target.value)} />
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Buy It Now Price ($)</label>
                <Input type="number" value={buyItNow} onChange={e => setBuyItNow(e.target.value)} placeholder="Optional" />
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Starting Bid ($)</label>
                <Input type="number" value={startingBid} onChange={e => setStartingBid(e.target.value)} />
              </div>

              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Min. Increment ($)</label>
                <Input type="number" value={increment} onChange={e => setIncrement(e.target.value)} />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Number of Bid Rows</label>
              <Input type="number" value={numRows} onChange={e => setNumRows(e.target.value)} />
            </div>
            
            <Button onClick={handlePrint} className="w-full gap-2 mt-4 bg-indigo-600 hover:bg-indigo-700 text-white">
              <Printer size={16} /> Print Bid Sheet
            </Button>
            <p className="text-[10px] text-slate-400 text-center uppercase tracking-widest mt-2">
              Note: The sidebar and these controls will be hidden on the printed page.
            </p>
          </div>
        </Card>
      </div>

      {/* Preview Panel - This becomes full width/screen on print */}
      <div className="space-y-6 print:absolute print:inset-0 print:w-full print:bg-white print:text-black">
        <Card className="p-8 bg-white text-slate-900 border-2 border-slate-200 shadow-sm print:border-none print:shadow-none print:p-0">
          
          <div className="text-center mb-8 border-b-2 border-slate-800 pb-6">
            <h2 className="text-3xl font-black uppercase tracking-widest mb-2">{orgName || 'Silent Auction'}</h2>
            <h2 className="text-5xl font-bold font-display italic text-indigo-900 print:text-black mb-4">{itemName || 'Item Name'}</h2>
            
            <div className="flex justify-center gap-8 mt-6">
              {itemValue && (
                <div className="bg-slate-100 print:bg-white print:border-2 print:border-slate-300 px-6 py-2 rounded-lg">
                  <span className="block text-xs uppercase font-bold text-slate-500 tracking-widest">Value</span>
                  <span className="text-2xl font-bold">${itemValue}</span>
                </div>
              )}
              {buyItNow && (
                <div className="bg-indigo-50 border-2 border-indigo-200 print:bg-white print:border-black px-6 py-2 rounded-lg">
                  <span className="block text-xs uppercase font-bold text-indigo-500 print:text-black tracking-widest">Buy It Now</span>
                  <span className="text-2xl font-black text-indigo-700 print:text-black">${buyItNow}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-between items-end mb-4 px-4">
             <div>
               <span className="text-sm font-bold uppercase tracking-widest block text-slate-500">Starting Bid:</span>
               <span className="text-xl font-bold">${start.toFixed(2)}</span>
             </div>
             <div className="text-right">
               <span className="text-sm font-bold uppercase tracking-widest block text-slate-500">Min. Increment:</span>
               <span className="text-xl font-bold">${inc.toFixed(2)}</span>
             </div>
          </div>

          <div className="overflow-hidden border-2 border-slate-800 rounded-lg print:rounded-none">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-800 text-white print:bg-gray-200 print:text-black">
                  <th className="py-3 px-4 w-16 text-center border-r border-slate-600 print:border-gray-400">#</th>
                  <th className="py-3 px-4 w-1/3 border-r border-slate-600 print:border-gray-400 uppercase tracking-widest text-xs">Bidder Name</th>
                  <th className="py-3 px-4 w-1/3 border-r border-slate-600 print:border-gray-400 uppercase tracking-widest text-xs">Phone / Email</th>
                  <th className="py-3 px-4 text-right uppercase tracking-widest text-xs">Bid Amount ($)</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {Array.from({ length: Math.min(rows, 35) }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-300 last:border-b-0 print:border-black">
                    <td className="py-4 px-4 text-center font-bold text-slate-400 border-r border-slate-300 print:border-black">{i + 1}</td>
                    <td className="py-4 px-4 border-r border-slate-300 print:border-black"></td>
                    <td className="py-4 px-4 border-r border-slate-300 print:border-black"></td>
                    <td className="py-4 px-4 text-right font-mono text-slate-400">
                      {inc > 0 ? (
                        <span>${(start + (i * inc)).toFixed(2)}</span>
                      ) : (
                        <span>$_________</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
              Please write clearly. Winning bidders will be contacted at the end of the event.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
